import { supabase } from '../supabaseClient'

function dataNoPeriodo(iso, dataInicio, dataFim) {
  if (!iso) return false
  const dia = String(iso).slice(0, 10)
  return dia >= dataInicio && dia <= dataFim
}

function donoLead(lead) {
  return lead?.responsavel_id || lead?.created_by || null
}

function isColunaNovo(col) {
  if (!col) return false
  if (col.slug === 'novo-lead') return true
  return String(col.titulo || '').trim().toLowerCase() === 'novo lead'
}

function isColunaContrato(col) {
  if (!col) return false
  return col.is_contrato_fechado === true || col.slug === 'contrato-fechado'
}

function kpisVazios() {
  return {
    criados: 0,
    atendidos: 0,
    semAtendimento: 0,
    convertidos: 0,
    valorConvertido: 0,
    emPipeline: 0
  }
}

function taxaPct(num, den) {
  if (!den) return 0
  return Math.round((num / den) * 1000) / 10
}

async function fetchAll(buildQuery, pageSize = 1000) {
  const rows = []
  let from = 0
  while (true) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1)
    if (error) throw error
    const chunk = data || []
    rows.push(...chunk)
    if (chunk.length < pageSize) break
    from += pageSize
  }
  return rows
}

function somarKpis(lista) {
  return lista.reduce((acc, row) => ({
    criados: acc.criados + row.criados,
    atendidos: acc.atendidos + row.atendidos,
    semAtendimento: acc.semAtendimento + row.semAtendimento,
    convertidos: acc.convertidos + row.convertidos,
    valorConvertido: acc.valorConvertido + row.valorConvertido,
    emPipeline: acc.emPipeline + row.emPipeline
  }), kpisVazios())
}

function enriquecerKpis(base) {
  const ticketMedio = base.convertidos > 0 ? base.valorConvertido / base.convertidos : 0
  return {
    ...base,
    taxaAtendimento: taxaPct(base.atendidos, base.criados),
    taxaConversao: taxaPct(base.convertidos, base.criados),
    taxaConversaoAtendidos: taxaPct(base.convertidos, base.atendidos),
    ticketMedio
  }
}

/**
 * Relatório de desempenho dos especialistas no Kanban Gestão Leads.
 * Filtro com 0 ou vários especialistas → agrupado; com 1 → individual.
 */
export async function montarRelatorioDesempenho({
  empresaId,
  dataInicio,
  dataFim,
  usuarioIds = null
}) {
  const filtroIds = Array.isArray(usuarioIds) && usuarioIds.length > 0
    ? new Set(usuarioIds)
    : null

  const [colunas, leads, historico, usuarios] = await Promise.all([
    fetchAll(() =>
      supabase
        .from('gestao_leads_colunas')
        .select('id, slug, titulo, is_contrato_fechado')
        .eq('empresa_id', empresaId)
    ),
    fetchAll(() =>
      supabase
        .from('gestao_leads')
        .select('id, nome, telefone, coluna_id, created_by, responsavel_id, created_at, ativo, cliente_efetivado, efetivado_em, valor_contrato')
        .eq('empresa_id', empresaId)
    ),
    fetchAll(() =>
      supabase
        .from('gestao_leads_historico')
        .select('id, lead_id, tipo_acao, coluna_anterior_id, coluna_nova_id, created_at')
        .eq('empresa_id', empresaId)
        .not('lead_id', 'is', null)
        .in('tipo_acao', ['mover', 'criar', 'cadastrar_contrato'])
        .gte('created_at', `${dataInicio}T00:00:00`)
        .lte('created_at', `${dataFim}T23:59:59.999`)
    ),
    supabase
      .from('usuarios_sistema')
      .select('id, nome, email, status')
      .eq('empresa_id', empresaId)
      .then(({ data, error }) => {
        if (error) throw error
        return data || []
      })
  ])

  const colunasMap = Object.fromEntries(colunas.map((c) => [c.id, c]))
  const leadsMap = Object.fromEntries(leads.map((l) => [l.id, l]))
  const nomesMap = Object.fromEntries(usuarios.map((u) => [u.id, u.nome || u.email || 'Especialista']))

  const grupos = new Map()

  const garantirGrupo = (especialistaId) => {
    const id = especialistaId || '__sem__'
    if (filtroIds && id !== '__sem__' && !filtroIds.has(id)) return null
    if (filtroIds && id === '__sem__') return null
    if (!grupos.has(id)) {
      grupos.set(id, {
        especialistaId: id === '__sem__' ? null : id,
        especialistaNome: id === '__sem__' ? 'Sem responsável' : (nomesMap[id] || 'Especialista'),
        criadosIds: new Set(),
        atendidosIds: new Set(),
        convertidosIds: new Set(),
        pipelineIds: new Set(),
        valorConvertido: 0,
        convertidosDetalhe: []
      })
    }
    return grupos.get(id)
  }

  const marcarAtendido = (lead) => {
    const g = garantirGrupo(donoLead(lead))
    if (g) g.atendidosIds.add(lead.id)
  }

  for (const lead of leads) {
    const g = garantirGrupo(donoLead(lead))
    if (!g) continue

    if (dataNoPeriodo(lead.created_at, dataInicio, dataFim)) {
      g.criadosIds.add(lead.id)
    }

    if (lead.cliente_efetivado && dataNoPeriodo(lead.efetivado_em || lead.created_at, dataInicio, dataFim)) {
      g.convertidosIds.add(lead.id)
      g.valorConvertido += Number(lead.valor_contrato) || 0
      g.convertidosDetalhe.push(lead)
    }

    if (lead.ativo && !lead.cliente_efetivado && !isColunaContrato(colunasMap[lead.coluna_id])) {
      g.pipelineIds.add(lead.id)
    }

    const col = colunasMap[lead.coluna_id]
    if (dataNoPeriodo(lead.created_at, dataInicio, dataFim) && col && !isColunaNovo(col)) {
      marcarAtendido(lead)
    }
  }

  for (const h of historico) {
    const lead = leadsMap[h.lead_id]
    if (!lead) continue
    const colNova = colunasMap[h.coluna_nova_id]
    if (h.tipo_acao === 'cadastrar_contrato') {
      marcarAtendido(lead)
      continue
    }
    if (h.tipo_acao === 'criar' && colNova && !isColunaNovo(colNova)) {
      marcarAtendido(lead)
      continue
    }
    if (h.tipo_acao === 'mover' && colNova && !isColunaNovo(colNova)) {
      marcarAtendido(lead)
    }
  }

  const linhas = [...grupos.values()]
    .map((g) => {
      const criados = g.criadosIds.size
      const atendidos = g.atendidosIds.size
      let semAtendimento = 0
      for (const id of g.criadosIds) {
        if (!g.atendidosIds.has(id) && !g.convertidosIds.has(id)) semAtendimento += 1
      }
      const base = {
        especialistaId: g.especialistaId,
        especialistaNome: g.especialistaNome,
        criados,
        atendidos,
        semAtendimento,
        convertidos: g.convertidosIds.size,
        valorConvertido: g.valorConvertido,
        emPipeline: g.pipelineIds.size,
        convertidosDetalhe: g.convertidosDetalhe.sort((a, b) => String(b.efetivado_em || '').localeCompare(String(a.efetivado_em || '')))
      }
      return enriquecerKpis(base)
    })
    .filter((row) =>
      row.criados > 0 || row.atendidos > 0 || row.convertidos > 0 || row.emPipeline > 0
    )
    .sort((a, b) => b.convertidos - a.convertidos || b.criados - a.criados || a.especialistaNome.localeCompare(b.especialistaNome, 'pt-BR'))

  const totais = enriquecerKpis(somarKpis(linhas))
  const modo = filtroIds && filtroIds.size === 1 ? 'individual' : 'agrupado'

  return {
    modo,
    dataInicio,
    dataFim,
    linhas,
    totais,
    individual: modo === 'individual' ? (linhas[0] || enriquecerKpis({
      ...kpisVazios(),
      especialistaId: [...filtroIds][0],
      especialistaNome: nomesMap[[...filtroIds][0]] || 'Especialista',
      convertidosDetalhe: []
    })) : null
  }
}

export function formatarMoedaRelatorio(valor) {
  return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}
